import CoinPicker from "@/components/battlefield/CoinPicker";
import RecentPairs from "@/components/battlefield/RecentPairs";
import LiveIndicator from "@/components/battlefield/LiveIndicator";
import ShareButton from "@/components/battlefield/ShareButton";
import { LegendFaqTrigger } from "@/components/battlefield/LegendFaqPanel";
import { Link } from "react-router-dom";
import { ArrowLeftRight, LayoutGrid, Video } from "lucide-react";

// Compact vertical HUD for mobile (< 768px viewport).
// Structure:
//   R1: pair title  |  LIVE dot  |  Share (icon-only)
//   R2: mid price + 24h % pill
//   R3: 24h vol muted
//   R4: coin picker A · VS · coin picker B  (compact, open bottom-sheet)
//   R5: recent pairs horizontal scroll
//   R6: route notice (conditional)

export default function MobileHud({
  pair,
  ticker,
  midPriceUi,
  wsStatus,
  coins,
  coinsLoading,
  onPickA,
  onPickB,
  recentPairs,
  onPickRecent,
  onRemoveRecent,
  onClearRecents,
  routeNotice,
  onSwapLeg,
  thinMarket,
  canvasRef,
  bookRef,
  onOpenFaq,
  faqPulse,
  autoOrbit,
  setAutoOrbit,
}) {
  const changeColor = ticker
    ? ticker.priceChangePercent >= 0
      ? "text-[#00FFA3] bg-[#025939]/40 border-[#00FFA3]/30"
      : "text-[#FF2A2A] bg-[#590909]/40 border-[#FF2A2A]/30"
    : "text-white bg-white/5 border-white/10";

  return (
    <div
      className="absolute top-0 left-0 right-0 z-40 flex flex-col gap-2 p-4 pt-5 pointer-events-none"
      data-testid="battlefield-hud"
    >
      {/* Row 1 — pair + LIVE + share */}
      <div className="flex items-center justify-between gap-2 pointer-events-auto">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span
            className="font-display text-2xl font-bold tracking-tighter text-white truncate"
            data-testid="pair-title"
          >
            {pair.base}
            <span className="text-slate-500 mx-1">/</span>
            {pair.quote}
          </span>
          {thinMarket && (
            <span
              className="cb-hud-chip px-2 py-0.5 font-mono-tac text-[9px] uppercase tracking-[0.24em] text-amber-400 !border-amber-400/40"
              data-testid="thin-market-label"
            >
              Thin
            </span>
          )}
        </div>
        <LiveIndicator status={wsStatus} compact />
        <ShareButton pair={pair} iconOnly />
        <LegendFaqTrigger onClick={() => onOpenFaq && onOpenFaq()} iconOnly pulse={!!faqPulse} />
        <button
          type="button"
          onClick={() => setAutoOrbit && setAutoOrbit((v) => !v)}
          aria-pressed={!!autoOrbit}
          aria-label="Toggle cinematic auto-orbit camera"
          data-testid="orbit-toggle"
          data-state={autoOrbit ? "on" : "off"}
          className={`cb-hud-chip flex items-center justify-center w-11 h-11 hover:bg-white/5 transition-colors shrink-0 ${
            autoOrbit ? "!border-[#FFE480]/50 !bg-[#FFE480]/10" : ""
          }`}
        >
          <Video className={`w-4 h-4 ${autoOrbit ? "text-[#FFE480]" : "text-white"}`} />
        </button>
        <Link
          to="/warroom"
          aria-label="Open War Room multi-pair view"
          data-testid="warroom-link"
          className="cb-hud-chip flex items-center justify-center w-11 h-11 hover:bg-white/5 transition-colors shrink-0"
        >
          <LayoutGrid className="w-4 h-4 text-white" />
        </Link>
      </div>

      {/* Row 2 — mid price + 24h % */}
      <div className="flex items-end justify-between gap-3 pointer-events-auto">
        <div className="flex flex-col leading-none">
          <span className="font-mono-tac text-[9px] uppercase tracking-[0.28em] text-slate-500">
            Mid Price
          </span>
          <span
            className="font-display text-3xl font-bold tracking-tighter text-white tabular-nums"
            data-testid="live-mid-price"
          >
            {formatMid(midPriceUi ?? ticker?.lastPrice)}
          </span>
        </div>
        <div
          className={`cb-hud-chip px-3 py-2 border ${changeColor} flex flex-col items-end leading-tight`}
          data-testid="live-24h-change-chip"
        >
          <span className="font-mono-tac text-[9px] uppercase tracking-[0.24em] opacity-70">
            24h
          </span>
          <span
            className="font-display text-lg font-bold tracking-tighter tabular-nums"
            data-testid="live-24h-change"
          >
            {ticker
              ? `${ticker.priceChangePercent >= 0 ? "+" : ""}${ticker.priceChangePercent.toFixed(2)}%`
              : "—"}
          </span>
        </div>
      </div>

      {/* Row 3 — 24h volume */}
      <div className="pointer-events-auto">
        <span
          className="font-mono-tac text-[10px] uppercase tracking-[0.24em] text-slate-500"
          data-testid="live-24h-volume"
        >
          24h Vol · {ticker ? formatVol(ticker.quoteVolume) : "—"} {pair.quote}
        </span>
      </div>

      {/* Row 4 — coin picker */}
      <div className="flex items-stretch gap-2 pointer-events-auto" data-testid="coin-picker-row">
        <CoinPicker
          coins={coins}
          value={pair.base}
          disabledSymbol={pair.quote}
          onChange={onPickA}
          side="left"
          loading={coinsLoading}
          mobile
          testIdPrefix="coin-picker-a"
        />
        <span className="font-display font-black text-white/70 text-lg tracking-tighter self-center px-1">
          VS
        </span>
        <CoinPicker
          coins={coins}
          value={pair.quote}
          disabledSymbol={pair.base}
          onChange={onPickB}
          side="right"
          loading={coinsLoading}
          mobile
          testIdPrefix="coin-picker-b"
        />
      </div>

      {/* Row 5 — recents horizontal scroll */}
      <div className="pointer-events-auto">
        <RecentPairs
          pairs={recentPairs}
          currentSymbol={pair.symbol}
          onPick={onPickRecent}
          onRemove={onRemoveRecent}
          onClear={onClearRecents}
          mobile
        />
      </div>

      {routeNotice && (
        <div
          className="cb-hud-chip flex items-center gap-2 px-3 py-2 border-white/20 pointer-events-auto"
          data-testid="route-notice"
        >
          <span className="font-mono-tac text-[9px] uppercase tracking-[0.24em] text-slate-400 shrink-0">
            Route
          </span>
          <span className="font-mono-tac text-[10px] text-white flex-1 truncate">
            No direct {routeNotice.a}/{routeNotice.b} ·{" "}
            <span className="font-bold">{pair.base}/{pair.quote}</span>
          </span>
          <button
            type="button"
            onClick={onSwapLeg}
            className="flex items-center gap-1 font-mono-tac text-[10px] uppercase tracking-[0.24em] text-[#00FFA3] hover:text-white transition-colors shrink-0"
            data-testid="route-swap-leg"
          >
            <ArrowLeftRight className="w-3 h-3" />
            Swap
          </button>
        </div>
      )}
    </div>
  );
}

function formatMid(n) {
  if (n == null || Number.isNaN(n)) return "—";
  const abs = Math.abs(n);
  const d = abs >= 100 ? 2 : abs >= 1 ? 4 : abs >= 0.01 ? 6 : 8;
  return n.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}

function formatVol(n) {
  if (n == null || Number.isNaN(n)) return "—";
  if (n >= 1e9) return (n / 1e9).toFixed(2) + "B";
  if (n >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(2) + "K";
  return n.toFixed(2);
}
